import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CognitoUser } from "amazon-cognito-identity-js";
import { QRCodeSVG } from "qrcode.react";
import React, { useState } from "react";
import { verifyTotpSetup } from "./auth";

interface MfaSetupProps {
  cognitoUser: CognitoUser;
  secretCode: string;
  email: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

export const MfaSetup: React.FC<MfaSetupProps> = ({
  cognitoUser,
  secretCode,
  email,
  onSuccess,
  onError,
}) => {
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate the otpauth URL for QR codes
  const otpauthUrl = `otpauth://totp/Bayescase%20Admin:${encodeURIComponent(
    email,
  )}?secret=${secretCode}&issuer=Bayescase%20Admin`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await verifyTotpSetup(cognitoUser, totpCode);
      onSuccess();
    } catch (err: any) {
      console.error("MFA setup error:", err);
      const errorMessage =
        err.message || "Failed to verify code. Please try again.";
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 450,
          width: "100%",
          mx: 2,
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Set Up Two-Factor Authentication
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
          align="center"
        >
          Scan the QR code below with your authenticator app (Google
          Authenticator, Authy, 1Password, etc.)
        </Typography>

        {/* QR Code */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: "white",
              border: "1px solid #ddd",
              borderRadius: 2,
            }}
          >
            <QRCodeSVG value={otpauthUrl} size={200} level="M" />
          </Box>
        </Box>

        {/* Manual entry option */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            gutterBottom
          >
            Can't scan? Enter this code manually:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              wordBreak: "break-all",
              fontWeight: "bold",
            }}
          >
            {secretCode}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Enter 6-digit code from app"
            type="text"
            inputMode="numeric"
            fullWidth
            value={totpCode}
            onChange={(e) => {
              // Only allow digits, max 6
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setTotpCode(value);
            }}
            disabled={loading}
            sx={{ mb: 3 }}
            autoFocus
            autoComplete="one-time-code"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading || totpCode.length !== 6}
          >
            {loading ? <CircularProgress size={24} /> : "Verify & Continue"}
          </Button>
        </form>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block", textAlign: "center" }}
        >
          Two-factor authentication is required for admin access to protect
          sensitive data.
        </Typography>
      </Paper>
    </Box>
  );
};
